import {useCallback, useEffect, useRef, useState} from 'react';

// The rig's live frames arrive 60 times a second: only the components that draw them re-render, never the editor.
const lights = {frame: null, subs: new Set()};
export const useLightsFrame = () => {
    const [frame, setFrame] = useState(lights.frame);
    useEffect(() => { lights.subs.add(setFrame); return () => lights.subs.delete(setFrame); }, []);
    return frame;
};
// Streams the editor has no use for (Zed's face frames, voice levels, cue echoes) never touch its state.
const IGNORED = new Set(['zed-frame', 'voice-level', 'cue']);

// Live link to a show engine's API: snapshot on connect, then every bus event. Reconnects forever.
export const useEngine = (url) => {
    const [connected, setConnected] = useState(false);
    const [state, setState] = useState(null);
    const [lastEvent, setLastEvent] = useState(null);
    const [playback, setPlayback] = useState(null);     // {block, startedAt, duration} while a block plays
    const [beat, setBeat] = useState(null);             // {bpm, phaseOrigin, beatsPerBar}
    const wsRef = useRef(null);
    const playingBlock = useRef(null);

    useEffect(() => {
        let closed = false;
        let timer;
        const connect = () => {
            const ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => setConnected(true);
            ws.onclose = () => {
                setConnected(false);
                if (!closed) timer = setTimeout(connect, 2000);
            };
            ws.onmessage = (m) => {
                const msg = JSON.parse(m.data);
                if (msg.type === 'snapshot') { setState(msg.state); setPlayback(msg.state.playback || null); playingBlock.current = msg.state.playback ? msg.state.playback.block : null; }
                if (msg.type !== 'event') return;
                const e = msg.event;
                if (e.type === 'lights-frame') { lights.frame = e.fixtures; lights.subs.forEach(fn => fn(e.fixtures)); return; }
                if (IGNORED.has(e.type)) return;
                if (e.type === 'beat') setBeat({bpm: e.bpm, phaseOrigin: e.phaseOrigin, beatsPerBar: e.beatsPerBar || 4});
                // a jump starts the next block before the last one's sound has stopped: a stale stop is ignored
                if (e.type === 'block-playback' && e.state === 'playing') {
                    playingBlock.current = e.block;
                    setPlayback({block: e.block, startedAt: e.startedAt, duration: e.duration});
                    if (e.beat) setBeat(e.beat);
                }
                if (e.type === 'block-playback' && e.state !== 'playing' && (playingBlock.current == null || playingBlock.current === e.block)) {
                    playingBlock.current = null;
                    setPlayback(null);
                    setBeat(null);
                }
                setLastEvent(e);
                if (e.type === 'scene') setState(s => s && {...s, sceneIndex: e.index, scene: e.scene});
                if (e.type === 'zed-performer') setState(s => s && {...s, zedPerformer: e.connected});
                if (e.type === 'mix') setState(s => s && {...s, mix: e.mix});
                if (e.type === 'roundEnd') setState(s => s && {...s, roundEnd: e.roundEnd});
                if (e.type === 'show-paused') setState(s => s && {...s, paused: e.paused});
            };
        };
        connect();
        return () => { closed = true; clearTimeout(timer); if (wsRef.current) wsRef.current.close(); };
    }, [url]);

    const send = useCallback((cmd) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(cmd));
    }, []);

    return {connected, state, lastEvent, playback, beat, send};
};
